/* eslint-disable @typescript-eslint/naming-convention */
import type { ThemeConfig } from 'lib/theme-config';
const APP_CONFIG_DEFAULT: ThemeConfig = {
  theme: {
    logoUrl: '/images/the-fictional-theatre.svg',
    fontFamily: 'Tixly',
    fontFaces: ['https://assets.tixly.com/fonts/tixly.css'],
    borderRadius: 'large',
    modalAndLayerRadius: 'large',
    containerRadius: 'medium',
    formItemRadius: 'medium',
    buttonRadius: 'full',
    buttonRipple: false,
    buttonTextTransform: 'none',
    palette: {
      primary: {
        main: '#FA4517',
        light: '#FC8F74',
        // Sunrise 300
        dark: '#C83712',
        // Sunrise 700
        contrastText: '#fff'
      },
      secondary: {
        main: '#25C1D9',
        light: '#7CDAE8',
        // Aqua 300
        dark: '#1E9AAE',
        // Aqua 700
        contrastText: '#fff'
      },
      error: {
        main: '#FA4517',
        light: '#FC8F74',
        // Sunrise 300
        dark: '#C83712',
        // Sunrise 700
        contrastText: '#fff',
        alertContent: '#C83712' // Sunrise 700
      },
      info: {
        main: '#0D5B8C',
        // Navy 500
        light: '#5289AC',
        // Navy 300
        dark: '#094164',
        // Navy 700
        contrastText: '#fff',
        alertContent: '#094164' // Navy 700
      },
      warning: {
        main: '#FE9418',
        light: '#FEBE74',
        // Orange 300
        dark: '#98580E',
        // Orange 700
        contrastText: '#fff',
        alertContent: '#98580E' // Orange 700
      },
      success: {
        main: '#10AF6A',
        light: '#6BC796',
        // Green 300
        dark: '#008E53',
        // Green 700
        contrastText: '#fff',
        alertContent: '#008E53' // Green 700
      },
      divider: 'rgba(0, 0, 0, 0.08)',
      action: {
        active: 'rgba(0, 0, 0, 0.44)',
        hover: 'rgba(0, 0, 0, 0.05)',
        selected: 'rgba(0, 0, 0, 0.08)',
        disabled: 'rgba(0, 0, 0, 0.14)',
        disabledBackground: 'rgba(0, 0, 0, 0.03)'
      },
      text: {
        primary: 'rgba(0, 0, 0, 0.87)',
        secondary: 'rgba(0,0,0,0.6)',
        disabled: 'rgba(0,0,0,0.4)'
      },
      background: {
        default: '#fff',
        paper: '#fff',
        elevated: '#fff'
      },
      monochrome: {
        light: 'rgba(0,0,0,0.02)',
        main: 'rgba(0,0,0,0.07)',
        dark: 'rgba(0,0,0,0.14)',
        contrastText: 'rgba(0, 0, 0, 0.87)',
        alertBackground: 'rgba(0,0,0,0.03)',
        outlineRestingBorder: 'rgba(0,0,0,0.12)'
      },
      white: {
        main: '#fff',
        contrastText: 'rgba(0, 0, 0, 0.87)'
      },
      black: {
        main: '#000',
        dark: '#313131',
        contrastText: '#fff'
      },
      outlinedBorder: {
        main: 'rgba(0, 0, 0, 0.16)'
      },
      backdropOverlay: {
        main: 'rgba(0, 0, 0, 0.5)'
      },
      filledInputBackground: {
        main: 'rgba(0, 0, 0, 0.09)'
      },
      standardInputLine: {
        main: 'rgba(0, 0, 0, 0.42)'
      },
      snackbarBackground: {
        main: 'rgba(0, 0, 0, 0.8)'
      },
      ratingActive: {
        main: '#FFBB00'
      },
      containerBackground: {
        main: 'rgba(0, 0, 0, 0.03)'
      }
    }
  },
  darkTheme: {
    logoUrl: '/images/the-fictional-theatre-dark.svg',
    palette: {
      primary: {
        main: '#FA4517',
        light: '#C83712',
        // Sunrise 300
        dark: '#FC8F74',
        // Sunrise 700
        contrastText: '#fff'
      },
      secondary: {
        main: '#25C1D9',
        light: '#1E9AAE',
        // Aqua 300
        dark: '#7CDAE8',
        // Aqua 700
        contrastText: '#fff'
      },
      error: {
        main: '#FA4517',
        light: '#C83712',
        // Sunrise 300
        dark: '#FC8F74',
        // Sunrise 700
        contrastText: '#fff',
        alertContent: '#FC8F74' // Sunrise 700
      },
      info: {
        main: '#2F729C',
        // Navy 600
        light: '#094164',
        // Navy 300
        dark: '#5289AC',
        // Navy 700
        contrastText: '#fff',
        alertContent: '#74A1BD' // Navy 800
      },
      warning: {
        main: '#FE9418',
        light: '#98580E',
        // Orange 300
        dark: '#FED4A2',
        // Orange 700
        contrastText: '#fff',
        alertContent: '#FED4A2' // Orange 700
      },
      success: {
        main: '#10AF6A',
        light: '#008E53',
        // Green 300
        dark: '#6BC796',
        // Green 700
        contrastText: '#fff',
        alertContent: '#6BC796' // Green 700
      },
      divider: 'rgba(255, 255, 255, 0.08)',
      action: {
        active: 'rgba(255, 255, 255, 0.44)',
        hover: 'rgba(255, 255, 255, 0.05)',
        selected: 'rgba(255, 255, 255, 0.16)',
        disabled: 'rgba(255, 255, 255, 0.14)',
        disabledBackground: 'rgba(255, 255, 255, 0.03)'
      },
      text: {
        primary: '#fff',
        secondary: 'rgba(255, 255, 255, 0.6)',
        disabled: 'rgba(255, 255, 255, 0.4)'
      },
      background: {
        default: '#121212',
        paper: '#202020',
        elevated: '#303030'
      },
      monochrome: {
        main: 'rgba(255,255,255,0.07)',
        light: 'rgba(255,255,255,0.02)',
        dark: 'rgba(255,255,255,0.14)',
        contrastText: 'rgba(255,255,255,0.87)',
        alertBackground: 'rgba(255,255,255,0.03)',
        outlineRestingBorder: 'rgba(255,255,255,0.12)'
      },
      white: {
        main: '#303030',
        contrastText: 'rgba(255,255,255,0.87)'
      },
      black: {
        main: '#000',
        dark: '#242424',
        contrastText: '#fff'
      },
      outlinedBorder: {
        main: 'rgba(255, 255, 255, 0.16)'
      },
      backdropOverlay: {
        main: 'rgba(0, 0, 0, 0.5)'
      },
      filledInputBackground: {
        main: 'rgba(255, 255, 255, 0.09)'
      },
      standardInputLine: {
        main: 'rgba(255, 255, 255, 0.42)'
      },
      snackbarBackground: {
        main: 'rgba(255, 255, 255, 0.8)'
      },
      ratingActive: {
        main: '#FFBB00'
      },
      containerBackground: {
        main: 'rgba(255, 255, 255, 0.03)'
      }
    }
  }
};
export const APP_CONFIG_ALTERNATIVE: ThemeConfig = {
  theme: {
    palette: {
      primary: {
        main: '#752393'
      },
      secondary: {
        main: '#FFCC00'
      }
    },
    borderRadius: 'small',
    modalAndLayerRadius: 'small',
    containerRadius: 'small',
    formItemRadius: 'small',
    buttonRadius: 'small',
    buttonRipple: false,
    logoUrl: '/images/the-fictional-theatre-purple.svg',
    fontFamily: 'Tixly',
    fontFaces: ['https://assets.tixly.com/fonts/tixly.css'],
    customStyles: {
      typography: {
        h1: {
          fontFamily: 'CrazyFont'
        }
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: `
          body {
            background-color: #f0f0f0;
          }
        `
        }
      }
    }
  }
};
export const APP_CONFIG_DESINGEL: ThemeConfig = {
  theme: {
    colorScheme: 'dark',
    palette: {
      ...APP_CONFIG_DEFAULT.darkTheme?.palette,
      primary: {
        main: '#c2fc50'
      },
      secondary: {
        main: '#888888'
      }
    },
    borderRadius: 'none',
    modalAndLayerRadius: 'none',
    containerRadius: 'none',
    formItemRadius: 'none',
    buttonRadius: 'none',
    buttonRipple: false,
    logoUrl: '/images/the-fictional-theatre-dark.svg',
    fontFamily: 'Tixly',
    fontFaces: ['https://assets.tixly.com/fonts/tixly.css'],
    buttonTextTransform: 'uppercase'
  }
};

/** Hardcoded visual themes the DEV config selector can swap in over the
 *  tenant's own theme. */
export const themePresets = {
  default: APP_CONFIG_DEFAULT,
  alternative: APP_CONFIG_ALTERNATIVE,
  desingel: APP_CONFIG_DESINGEL
};
export type ThemePresetId = keyof typeof themePresets;